import styled from 'styled-components'

import { media } from 'styles/media'

import SvgIconCalendar from 'components/icons/CalendarBookingsDetailed'
import SvgIconCheckCircle from 'components/icons/CheckCircle'
import { ParagraphSm } from 'components/display/Typography'
import { Hint } from 'components/form'

type Option = {
  label: string
  value: string
}

const CheckIconWrapper = styled.div<{ $checked: boolean }>`
  width: 22px;
  height: 22px;
  position: absolute;
  top: 8px;
  right: 8px;

  svg {
    width: 22px;
    height: 22px;
    color: ${({ theme, $checked }) =>
      $checked
        ? theme?.['-ds-core-400']?.value
        : theme?.['-ds-neutral-300']?.value};
  }

  ${media.md`
    position: relative;
    margin-left: auto;
    margin-left: 0;
    margin-top: 16px;
    top: unset;
    right: unset;
  `}
`

const CalendarIconWrapper = styled.div`
  display: none;

  ${media.md`
    display: block;
    width: 30px;
    height: 28px;
    margin: 0 0 10px 0;

    svg {
      width: 30px;
      height: 28px;
      margin: 0;
      color: ${({ theme }) => theme?.['-ds-neutral-700']?.value};
    }
  `}
`

const Options = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 16px;

  margin-top: 24px;

  input[type='checkbox'] {
    position: absolute;
    visibility: hidden;
  }

  input[type='checkbox'] + label {
    border: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
  }

  input[type='checkbox']:checked + label {
    border: 1px solid ${({ theme }) => theme?.['-ds-core-400']?.value};
    background-color: ${({ theme }) => theme?.['-ds-success-100']?.value};
  }

  ${media.md`
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  `}
`

const MonthWrapper = styled.div``

const MonthLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  position: relative;
  cursor: pointer;
  user-select: none;

  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};
  font-weight: 400;
  padding: 16px;
  border-radius: 8px;

  ${media.md`
    flex-direction: column;
    text-align: center;
    align-items: center;
  `}
`

type ChangeInput = { value: string; checked: boolean }

type Props = {
  selectedValues: string[]
  onChange: (_: ChangeInput) => any
}

const AvailableWeekendsInput = ({ selectedValues, onChange }: Props) => {
  const options: Option[] = [...Array(5)].map((_, index) => {
    const now = new Date()
    const optionDate = new Date(now.setMonth(now.getMonth() + index))
    const month = optionDate.getMonth() + 1 // month starts at 0
    const twoDigitMonth = month.toString().padStart(2, '0')
    const year = optionDate.getFullYear()

    const label = new Intl.DateTimeFormat('en-us', {
      month: 'long',
      year: 'numeric',
    }).format(optionDate)

    return {
      label,
      value: `${year}-${twoDigitMonth}`,
    }
  })

  return (
    <>
      <ParagraphSm style={{ fontWeight: 400 }}>
        Find escapes with an available weekend in...
      </ParagraphSm>
      <Hint>
        Weekends are considered Friday to Sunday (2-night stay). Long weekends
        and holidays may have longer minimum stays, so use specific dates when
        booking for these periods.
      </Hint>

      <Options>
        {options.map(({ label, value }) => {
          const checked = selectedValues.includes(value)
          return (
            <MonthWrapper key={label}>
              <input
                type="checkbox"
                id={label}
                name={label}
                value={value}
                onChange={(event) =>
                  onChange({
                    checked: event.target.checked,
                    value,
                  })
                }
                checked={checked}
              />
              <MonthLabel htmlFor={label}>
                <CalendarIconWrapper>
                  <SvgIconCalendar />
                </CalendarIconWrapper>

                <div>
                  {label.split(' ').map((word) => (
                    <p key={word} style={{ margin: 0 }}>
                      {word}
                    </p>
                  ))}
                </div>

                <CheckIconWrapper $checked={checked}>
                  <SvgIconCheckCircle />
                </CheckIconWrapper>
              </MonthLabel>
            </MonthWrapper>
          )
        })}
      </Options>
    </>
  )
}

export default AvailableWeekendsInput
